/**
 * Core Chaupar Game Types
 *
 * All TypeScript types and interfaces for the Chaupar game.
 * Board: cross-shaped with shared track (68 squares), central Charkoni,
 * 4 home columns (6 squares each), and central home.
 *
 * Key differences from Pachisi:
 * - Uses 3 long dice (pāsā) with values [1, 2, 5, 6] instead of cowrie shells
 * - NO safe squares — any piece can be captured anywhere
 * - Captures ELIMINATE permanently (not sent back to start)
 * - Team-based: Red+Green vs Blue+Yellow
 * - Blocking: 2 same-TEAM pawns (including partner's) form impassable block
 * - Special triples grant extra turns + bonus moves
 * - Pawns start on the track at historically authentic positions (no entry mechanic)
 */

// ============================================================================
// Player & Game Configuration
// ============================================================================

export type PlayerColor = 'red' | 'blue' | 'green' | 'yellow';
export type TeamColor = 'teamA' | 'teamB'; // A: Red+Green, B: Blue+Yellow
export type ChauparDifficulty = 'easy' | 'medium' | 'hard';
export type PlayerType = 'human' | 'ai';

// ============================================================================
// Dice Types
// ============================================================================

/** Result of rolling 3 long dice (pāsā) */
export interface DiceResult {
  values: [number, number, number]; // Three dice, each from DICE_VALUES [1,2,5,6]
  isTriple: boolean;
  bonusValue: number; // 0 normally, 10 for triple-1s, 18 for triple-6s
  player: PlayerColor;
}

// ============================================================================
// Pawn Types
// ============================================================================

/** Where a pawn currently resides */
export type PawnLocation = 'track' | 'home_column' | 'home' | 'eliminated';

/** Position of a single pawn */
export interface PawnPosition {
  pawnIndex: number;           // 0-3
  location: PawnLocation;
  trackPosition?: number;      // 0-67 on perimeter track
  homeColumnPosition?: number; // 0-5 on home column
}

/** Identifies a specific pawn */
export interface PawnIdentifier {
  player: PlayerColor;
  pawnIndex: number;
}

/** Full info about a pawn including its owner */
export interface PawnInfo {
  player: PlayerColor;
  pawnIndex: number;
  position: PawnPosition;
}

/** Board position for rendering (row/col on the visual grid) */
export interface BoardPosition {
  row: number;
  col: number;
}

// ============================================================================
// Move Types
// ============================================================================

export interface ChauparMove {
  player: PlayerColor;
  pawnIndex: number;
  dieIndex: number;    // Which die (0, 1, or 2) is being used
  dieValue: number;    // The value of that die
  action: 'move' | 'skip';
}

export interface TurnMoveSet {
  moves: ChauparMove[];    // Up to 3 moves (one per die) + bonus
  bonusMove?: ChauparMove; // If triple grants bonus
}

export interface MoveValidation {
  valid: boolean;
  reason?: 'not_your_turn' | 'invalid_pawn' | 'blocked_by_block' |
           'exceeds_home' | 'pawn_already_home' | 'pawn_eliminated' | 'game_over' |
           'die_already_used' | 'path_blocked';
}

export interface MoveResult {
  success: boolean;
  move: ChauparMove;
  newPosition: PawnPosition;
  captured?: PawnInfo;
  reachedHome: boolean;
  error?: string;
}

export interface TurnResult {
  success: boolean;
  moveResults: MoveResult[];
  extraTurn: boolean;
  gameOver: boolean;
  winningTeam?: TeamColor;
}

export interface MoveRecord {
  moveNumber: number;
  player: PlayerColor;
  dieIndex: number;
  dieValue: number;
  pawnIndex: number;
  action: 'move' | 'skip';
  fromPosition: PawnPosition;
  toPosition: PawnPosition;
  captured?: PawnInfo;
  reachedHome: boolean;
  timestamp: number;
}

// ============================================================================
// Game State
// ============================================================================

export type ChauparGameStatus =
  | { type: 'active' }
  | { type: 'win'; winningTeam: TeamColor }
  | { type: 'resignation'; winningTeam: TeamColor; resigned: PlayerColor };

export interface ChauparGameState {
  pawns: Record<PlayerColor, PawnPosition[]>;
  eliminatedPawns: Record<PlayerColor, number>; // Count of eliminated pawns per player
  activePlayers: PlayerColor[];
  playerTypes: Record<PlayerColor, PlayerType>;
  teams: Record<TeamColor, PlayerColor[]>;
  currentTurn: PlayerColor;
  lastDiceResult: DiceResult | null;
  usedDice: boolean[];      // Which of the 3 dice have been used this turn
  awaitingMove: boolean;
  pawnsHome: Record<PlayerColor, number>;
  moveHistory: MoveRecord[];
  gameStatus: ChauparGameStatus;
  gameId: string;
  startedAt: number;
  lastMoveAt: number;
}

// ============================================================================
// Game Result & Statistics
// ============================================================================

export interface ChauparGameResult {
  gameId: string;
  mode: ChauparGameSettings['mode'];
  difficulty?: ChauparDifficulty;
  winningTeam?: TeamColor;
  reason: 'all_pawns_home' | 'all_opponents_eliminated' | 'resignation';
  totalMoves: number;
  capturesMade: number;
  specialCombinations: number;
  duration: number;
  completedAt: number;
}

export interface ChauparGameStats {
  wins: number;
  losses: number;
  winPercentage: number;
  averageGameLength: number;
  averageCapturesPerGame: number;
}

export interface ChauparStatistics {
  userId?: string;
  singlePlayer: {
    easy: ChauparGameStats;
    medium: ChauparGameStats;
    hard: ChauparGameStats;
  };
  localMultiplayer: ChauparGameStats;
  onlineMultiplayer: ChauparGameStats;
  totalGames: number;
  totalPlayTime: number;
}

// ============================================================================
// Game Settings
// ============================================================================

export interface ChauparGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  aiDifficulty?: ChauparDifficulty;
  isPrivate?: boolean;
}

// ============================================================================
// Component Props
// ============================================================================

export interface ChauparGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

export interface ChauparGameModeSelectorProps {
  onStartGame: (settings: ChauparGameSettings) => void;
  onJoinPrivateGame?: (roomCode: string) => Promise<void>;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
  isOnline?: boolean;
}

// ============================================================================
// Animation & Multiplayer
// ============================================================================

export interface PawnAnimation {
  player: PlayerColor;
  pawnIndex: number;
  fromPosition: BoardPosition;
  toPosition: BoardPosition;
  type: 'move' | 'capture' | 'home';
}

export interface TurnSkipInfo {
  player: PlayerColor;
  reason: 'no_valid_moves';
  nextTurn: PlayerColor;
}

// ============================================================================
// Multiplayer Types
// ============================================================================

export interface ChauparMultiplayerMove {
  type: 'roll_request' | 'dice_result' | 'turn_moves';
  player: PlayerColor;
  diceResult?: DiceResult;
  moves?: TurnMoveSet;
  timestamp: number;
}

export interface ChauparRoomState {
  roomCode: string;
  players: { id: string; color: PlayerColor; connected: boolean }[];
  gameState: ChauparGameState | null;
  status: 'waiting' | 'in_progress' | 'completed';
  createdAt: number;
}

// ============================================================================
// Constants
// ============================================================================

export const TRACK_LENGTH = 68;
export const HOME_COLUMN_LENGTH = 6;
export const PAWNS_PER_PLAYER = 4;
export const NUM_DICE = 3;
export const PLAYERS_PER_TEAM = 2;

export const PLAYER_COLORS: PlayerColor[] = ['red', 'blue', 'green', 'yellow'];

export const TEAMS: Record<TeamColor, PlayerColor[]> = {
  teamA: ['red', 'green'],
  teamB: ['blue', 'yellow'],
};

/** Starting positions on the shared track (absolute positions 0-67) */
export const PLAYER_START_POSITIONS: Record<PlayerColor, number> = {
  red: 0,
  blue: 17,
  green: 34,
  yellow: 51,
};

/** Home column entry points — the last track position before entering home column */
export const HOME_COLUMN_ENTRY: Record<PlayerColor, number> = {
  red: 66,
  blue: 15,
  green: 32,
  yellow: 49,
};

/** Historically authentic dice values for Chaupar (pāsā long dice) */
export const DICE_VALUES: number[] = [1, 2, 5, 6];

/** Pawn starting offsets from each player's start position (historically authentic)
 * Adjusted so pawns appear symmetrically on each player's arm:
 * 2 pawns in the outgoing lane (posInArm 2,4) and 2 in the returning lane (posInArm 10,12)
 */
export const PAWN_START_OFFSETS: number[] = [2, 4, 10, 12];

/** Special dice combinations: triple value → bonus move value */
export const TRIPLE_BONUS: Record<number, number> = {
  1: 10,  // Triple 1s: bonus move value 10
  6: 18,  // Triple 6s: bonus move value 18
};
